/* eslint-disable react/no-unescaped-entities */
/* Sales > E-Ticaret tab (Iter 97 — Bol.com siparisleri).
   - Sync butonu (manuel) — Bol.com'dan tum siparisleri ceker
   - Liste: tarih, musteri, urunler, tutar, fatura istegi rozet, Billit durumu
   - Sadece fatura isteyenleri goster filter
   - Tek tek veya toplu "Billit'e Aktar" (oto fatura)
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
    RefreshCw, Loader2, FileCheck, ShoppingCart, ExternalLink,
    AlertCircle, CheckCircle2, Settings as SettingsIcon, Truck,
    ChevronLeft, ChevronRight, BarChart3, Wallet, Download, Printer, X,
    MoreVertical, MessageSquare,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BolDashboard from './BolDashboard';
import CustomerQATab from './parts/CustomerQATab';
import BolProfitability from './BolProfitability';
import {
    useBolOrders,
    useBolPreviewLabels,
    useBolCreateLabels,
    useBolSyncMeta,
    useBolRecoverLabels,
    useBolConfig,
} from '../../hooks/useBol';
import OrderDetailRow from './parts/OrderDetailRow';
import LabelPreviewDialog from './parts/LabelPreviewDialog';
import DiagnoseDialog from './parts/DiagnoseDialog';
import PrintTargetDialog from './parts/PrintTargetDialog';
import PrintNodeSettings from './parts/PrintNodeSettings';
import AutoPrintSettings from './parts/AutoPrintSettings';
import AutoPrintBanner from './parts/AutoPrintBanner';
import OrderActions from './parts/OrderActions';
import EcommerceHeader from './parts/EcommerceHeader';
import EcommerceFilters from './parts/EcommerceFilters';
import BulkActionBar from './parts/BulkActionBar';
import { useBpostConfig, useCreateBpostLabel, useDeleteBpostLabel } from '../../hooks/useBpost';
import { API, hdrs } from '../../lib/api';

const fmt = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDt = (o) => {
    if (o?.order_datetime) return o.order_datetime.slice(0, 16).replace('T', ' ');
    return o?.order_date || '';
};

// Iter 97.4: Bol transport event codes (tum kargo durumlari)
const EVENT_LABEL = {
    PRE_ANNOUNCED: { tr: 'Bildirildi', tone: 'gray' },
    INBOUND_COLLECT: { tr: 'Alındı', tone: 'sky' },
    AT_TRANSPORTER: { tr: 'Kargoda', tone: 'sky' },
    IN_TRANSIT: { tr: 'Yolda', tone: 'sky' },
    AT_PICK_UP_POINT: { tr: 'Pakomata Bekliyor', tone: 'amber' },
    OUT_FOR_DELIVERY: { tr: 'Dağıtımda', tone: 'amber' },
    DELIVERY_ATTEMPT: { tr: 'Teslim Denendi', tone: 'amber' },
    DELIVERED: { tr: 'Teslim Edildi', tone: 'emerald' },
    DELIVERED_AT_CUSTOMER: { tr: 'Teslim Edildi', tone: 'emerald' },
    DELIVERED_AT_NEIGHBOURS: { tr: 'Komşuya Teslim', tone: 'emerald' },
    DELIVERED_AT_PICKUP_POINT: { tr: 'Pakomatadan Teslim', tone: 'emerald' },
    DELIVERED_AT_SERVICE_POINT: { tr: 'Şubeye Teslim', tone: 'emerald' },
    RETURNED_TO_SENDER: { tr: 'Geri Döndürüldü', tone: 'rose' },
    CANCELLED: { tr: 'İptal Edildi', tone: 'rose' },
};
const TONE_CLS = {
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    sky: 'bg-sky-100 text-sky-800 border-sky-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    rose: 'bg-rose-100 text-rose-800 border-rose-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200',
};

function TrackBadge({ tracking, listStatus, testId }) {
    const t = tracking || {};
    // Backward-compat: eski siparişler tracking.events[*].code formatında latest_event olmadan saklanmış olabilir
    let latestEvent = t.latest_event;
    if (!latestEvent && Array.isArray(t.events) && t.events.length > 0) {
        // events array'inde en yeni tarihli event'in code'unu al
        const sorted = [...t.events].filter((e) => e && (e.code || e.eventCode))
            .sort((a, b) => (b.at || b.eventDateTime || '').localeCompare(a.at || a.eventDateTime || ''));
        if (sorted.length > 0) {
            latestEvent = (sorted[0].code || sorted[0].eventCode || '').toUpperCase();
        }
    }
    if (latestEvent) {
        const meta = EVENT_LABEL[latestEvent] || { tr: latestEvent, tone: 'gray' };
        return (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${TONE_CLS[meta.tone]}`}
                data-testid={testId}>
                <Truck size={10} /> {meta.tr}
            </span>
        );
    }
    // Sadece etiket basılı, henüz transport event'i yok → dürüst "Etiket Basıldı"
    if (listStatus === 'shipped' || t.track_and_trace || t.code) {
        return (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${TONE_CLS.gray}`}
                data-testid={testId}>
                <Truck size={10} /> Etiket Basıldı
            </span>
        );
    }
    if (listStatus === 'cancelled') {
        return (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${TONE_CLS.rose}`}
                data-testid={testId}>İptal</span>
        );
    }
    return <span className="text-[11px] text-gray-400" data-testid={testId}>Beklemede</span>;
}

const PAGE_SIZE = 20;

export default function EcommerceTab({ hideHeader = false } = {}) {
    const navigate = useNavigate();
    const qc = useQueryClient();
    // Iter 110/Faz3: useBolConfig React Query hook'una taşındı
    const configQuery = useBolConfig();
    const config = configQuery.data;
    const [syncing, setSyncing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [pushingId, setPushingId] = useState(null);
    const [bulkPushing, setBulkPushing] = useState(false);
    const [onlyPending, setOnlyPending] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [expanded, setExpanded] = useState(null);
    const [statusTab, setStatusTab] = useState('open');  // new | open | shipped | all | dashboard
    const [labelPreview, setLabelPreview] = useState(null);  // legacy, kullanılmıyor
    const [diagResult, setDiagResult] = useState(null);
    const [diagLoading, setDiagLoading] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [page, setPage] = useState(1);
    // Iter 109: split shipment akışı kaldırıldı; bpost destek etiketi yeterli
    const bpostCfgQuery = useBpostConfig();
    const bpostConfigured = !!bpostCfgQuery.data?.configured;
    const createBpostMut = useCreateBpostLabel();
    const deleteBpostMut = useDeleteBpostLabel();
    const [bpostBusyOrder, setBpostBusyOrder] = useState(null);
    // Iter 104e: PrintNode — yazdırma sonrası açılan target dialog
    const [printTarget, setPrintTarget] = useState(null);  // {blob, title, copies?}

    // Iter 104: React Query — server state hook'lar
    // Iter 110/Faz3: queryKey useMemo ile referans stabilizasyonu
    const ordersQueryKey = useMemo(() => ['bol-orders', {
        status: statusTab === 'all' ? null : statusTab,
        limit: 1000,
        onlyPending,
    }], [statusTab, onlyPending]);
    const ordersQuery = useBolOrders({
        status: statusTab === 'all' ? null : statusTab,
        limit: 1000,
        onlyPending,
    });
    const orders = ordersQuery.data?.items || [];
    const counts = ordersQuery.data?.counts || { all: 0, new: 0, open: 0, shipped: 0, invoice_requested: 0 };
    const loading = ordersQuery.isLoading;
    const load = ordersQuery.refetch;

    // Iter 113.29: Hazırlanacak/Hazırlandı tab'ları için ek state
    const [pickingItems, setPickingItems] = useState({ pending: [], picked: [] });
    const [pickingStats, setPickingStats] = useState({ pending: 0, picked_today: 0 });
    const [pickingLoading, setPickingLoading] = useState(false);
    const loadPicking = useCallback(async () => {
        setPickingLoading(true);
        try {
            const H = { Authorization: `Bearer ${localStorage.getItem('token') || ''}` };
            const [p, c, s] = await Promise.all([
                axios.get(`${API}/api/bol/picking/pending`, { headers: H }),
                axios.get(`${API}/api/bol/picking/completed`, { headers: H, params: { limit: 500, today_only: false } }),
                axios.get(`${API}/api/bol/picking/stats`, { headers: H }),
            ]);
            setPickingItems({
                pending: p.data?.items || [],
                picked: c.data?.items || [],
            });
            setPickingStats(s.data || { pending: 0, picked_today: 0 });
        } catch { /* ignore */ }
        finally { setPickingLoading(false); }
    }, []);
    useEffect(() => {
        if (statusTab === 'pick_pending' || statusTab === 'picked') {
            loadPicking();
            const iv = setInterval(loadPicking, 5000);
            return () => clearInterval(iv);
        }
    }, [statusTab, loadPicking]);
    const unpickOne = async (order_id) => {
        try {
            const H = { Authorization: `Bearer ${localStorage.getItem('token') || ''}` };
            await axios.post(`${API}/api/bol/picking/${order_id}/unpick`, {}, { headers: H });
            toast.success(`${order_id} geri alındı`);
            loadPicking();
        } catch (e) { toast.error(e.response?.data?.detail || 'Geri alınamadı'); }
    };

    // Mutation hooks
    const previewMutation = useBolPreviewLabels();
    const createMutation = useBolCreateLabels();
    const syncMetaMutation = useBolSyncMeta();
    const recoverMutation = useBolRecoverLabels();
    const printingLabels = createMutation.isPending || recoverMutation.isPending;
    const recovering = recoverMutation.isPending;
    // Per-order print tracking — sadece aktif yazdırılan siparişin butonu disabled olur
    const [printingOrderIds, setPrintingOrderIds] = useState(() => new Set());

    // Helper: orders cache'i local patch için
    const patchOrders = useCallback((fn) => {
        qc.setQueryData(ordersQueryKey, (old) => {
            if (!old) return old;
            return { ...old, items: fn(old.items || []) };
        });
    }, [qc, ordersQueryKey]);
    const patchCounts = useCallback((fn) => {
        qc.setQueryData(ordersQueryKey, (old) => {
            if (!old) return old;
            return { ...old, counts: fn(old.counts || {}) };
        });
    }, [qc, ordersQueryKey]);

    // Config tek seferlik fetch artık useBolConfig hook'unda — Iter 110/Faz3

    const sync = async () => {
        setSyncing(true);
        try {
            const r = await axios.post(`${API}/api/bol/sync`, {}, { headers: hdrs(), timeout: 120000 });
            const d = r.data;
            toast.success(
                `${d.upserted} siparis senkronize edildi`,
                { duration: 5000 },
            );
            // Iter 97.2: Bol Invoice Requests izni yoksa sessiz gec (kullanici biliyor zaten)
            // Iter 100: 429 rate-limit ve detail çağrı hatalarını da sessiz geç (otomatik retry zaten yapılıyor)
            const permissionWarn = (d.errors || []).find((e) => typeof e === 'string' && (
                e.startsWith('BOL_PERMISSION:') ||
                e.startsWith('BOL_INVOICES_PERMISSION:') ||
                e.startsWith('BOL_ADV_NOT_CONFIGURED:') ||
                e.startsWith('BOL_ADV_PERMISSION:') ||
                e.includes('429') ||
                e.includes('detail ')
            ));
            if (!permissionWarn && d.errors?.length) {
                toast.warning(`Uyari: ${d.errors[0]}`, { duration: 6000 });
            }
            if (typeof d.invoices_fetched === 'number' && d.invoices_fetched > 0) {
                toast.info(`${d.invoices_fetched} Bol faturasi (komisyon/kargo) çekildi`, { duration: 4000 });
            }
            // Etiket/tracking bilgilerini de güncelle (shipment'lerden) — Iter 104: hook üzerinden
            try {
                const meta = await syncMetaMutation.mutateAsync();
                if (meta?.updated > 0) {
                    toast.success(`${meta.updated} sipariş için etiket bilgisi güncellendi`, { duration: 4000 });
                }
            } catch { /* sessiz */ }
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Sync hatasi');
        } finally {
            setSyncing(false);
        }
    };

    // Iter 116: async background job pattern (Cloudflare 100s timeout bypass)
    const [trackingRefreshing, setTrackingRefreshing] = useState(false);
    const refreshTracking = async () => {
        setTrackingRefreshing(true);
        try {
            // 1) Start background job, returns immediately
            const startResp = await axios.post(`${API}/api/bol/tracking/refresh`, {}, {
                headers: hdrs(), timeout: 20000,
            });
            const jobId = startResp.data?.job_id;
            if (!jobId) throw new Error('Job ID alınamadı');
            toast.info('Kargo durumları çekiliyor... (1-3 dakika sürebilir)');
            // 2) Poll every 3s for up to 5min
            const startTs = Date.now();
            const maxWaitMs = 5 * 60 * 1000;
            let result = null;
            while (true) {
                if (Date.now() - startTs > maxWaitMs) throw new Error('Çok uzun sürdü');
                await new Promise((r) => setTimeout(r, 3000));
                let st;
                try {
                    st = await axios.get(`${API}/api/bol/tracking/refresh-job/${jobId}`, {
                        headers: hdrs(), timeout: 15000,
                    });
                } catch (pe) {
                    if (pe.response?.status === 404) throw new Error('Job kaybedildi');
                    continue;
                }
                if (st.data?.status === 'done') { result = st.data; break; }
                if (st.data?.status === 'error') throw new Error(st.data?.error || 'Hata');
            }
            toast.success(`${result.updated}/${result.scanned} sipariş için kargo durumu güncellendi`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || 'Tracking güncellenmedi');
        } finally {
            setTrackingRefreshing(false);
        }
    };

    const exportExcel = async () => {
        setExporting(true);
        try {
            const params = statusTab && statusTab !== 'all' && ['new', 'open', 'shipped', 'cancelled'].includes(statusTab)
                ? `?status=${statusTab}` : '';
            const r = await axios.get(`${API}/api/bol/orders/export.xlsx${params}`, {
                headers: hdrs(),
                responseType: 'blob',
                timeout: 60000,
            });
            const url = URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url;
            const disp = r.headers['content-disposition'] || '';
            const m = disp.match(/filename="([^"]+)"/);
            a.download = m ? m[1] : `bol_siparisler_${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Excel indirildi (${r.headers['x-total-rows'] || '?'} satir)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Excel export hatasi');
        } finally {
            setExporting(false);
        }
    };

    const pushOne = async (order) => {
        setPushingId(order.order_id);
        try {
            const r = await axios.post(
                `${API}/api/bol/orders/${order.order_id}/push-billit`,
                {}, { headers: hdrs(), timeout: 60000 },
            );
            const d = r.data;
            if (d.already_pushed) toast.info('Bu siparis zaten Billit\'e gonderilmis');
            else toast.success(`Billit fatura olusturuldu (#${d.billit_order_id})`);
            // Optimistic update
            patchOrders((cur) => cur.map((o) => o.order_id === order.order_id
                ? { ...o, billit_pushed: true, billit_order_id: d.billit_order_id, acc_invoice_id: d.acc_invoice_id }
                : o));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Billit aktarma hatasi');
        } finally {
            setPushingId(null);
        }
    };

    const pushSelected = async () => {
        if (selected.size === 0) return;
        if (!window.confirm(`${selected.size} siparis Billit'e aktarilacak. Devam?`)) return;
        setBulkPushing(true);
        // Faz0/Iter 110: Paralel push (Promise.allSettled) — 20 sipariş için 40s → 4s
        const ids = Array.from(selected);
        const results = await Promise.allSettled(ids.map((orderId) =>
            axios.post(
                `${API}/api/bol/orders/${orderId}/push-billit`,
                {}, { headers: hdrs(), timeout: 60000 },
            ).then((r) => ({ orderId, ...r.data })),
        ));
        let ok = 0, fail = 0;
        const patches = [];
        for (const res of results) {
            if (res.status === 'fulfilled') {
                if (!res.value.already_pushed) ok++;
                patches.push({
                    order_id: res.value.orderId,
                    billit_order_id: res.value.billit_order_id,
                });
            } else {
                fail++;
            }
        }
        if (patches.length) {
            patchOrders((cur) => cur.map((o) => {
                const p = patches.find((x) => x.order_id === o.order_id);
                return p ? { ...o, billit_pushed: true, billit_order_id: p.billit_order_id } : o;
            }));
        }
        toast.success(`${ok} aktarildi${fail ? ` · ${fail} hata` : ''}`, { duration: 6000 });
        setSelected(new Set());
        setBulkPushing(false);
    };

    const printPickSlips = useCallback(async (orderIds) => {
        if (!orderIds || orderIds.length === 0) {
            toast.error('Sipariş seçilmedi');
            return;
        }
        try {
            const r = await axios.post(`${API}/api/bol/labels/pickslip`,
                { order_ids: orderIds },
                { headers: hdrs(), responseType: 'blob', timeout: 60000 },
            );
            const blob = new Blob([r.data], { type: 'application/pdf' });
            const count = r.headers['x-pickslips-count'] || orderIds.length;
            setPrintTarget({ blob, title: `Pick Slips (${count})` });
            setSelected(new Set());
        } catch (e) {
            const detail = e.response?.data instanceof Blob
                ? await e.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return t; } })
                : (e.response?.data?.detail || 'Pick slip basma hatası');
            toast.error(typeof detail === 'string' ? detail.slice(0, 200) : 'Pick slip basma hatası');
        }
    }, []);

    // ─── BPOST DESTEK ETİKETİ ─────────────────────────────────────────────
    const printBpostLabel = useCallback(async (order, { force = false } = {}) => {
        if (!order?.order_id) return;
        // Iter 110/Faz0: Idempotency — mevcut etiket varsa kullanıcıyı uyar
        if (order.bpost_label?.client_ref && !force) {
            const choice = window.confirm(
                `Bu siparişin mevcut bpost etiketi var:\n` +
                `Tracking: ${order.bpost_label.track || '-'}\n\n` +
                `OK = Mevcut etiketi yeniden yazdır (ÜCRETSİZ)\n` +
                `İptal = Hiçbir şey yapma\n\n` +
                `Yeni bir etiket basmak istiyorsan önce "X" ile sil.`,
            );
            if (!choice) return;
        }
        setBpostBusyOrder(order.order_id);
        try {
            const r = await createBpostMut.mutateAsync({ orderId: order.order_id, force });
            const ct = (r.headers['content-type'] || '').toLowerCase();
            // bpost endpoint başarısızsa 200 + JSON döner
            if (ct.includes('application/json')) {
                let body = {};
                try {
                    const text = await r.data.text();
                    body = JSON.parse(text);
                } catch { /* ignore */ }
                toast.error(body.message || 'bpost etiketi oluşturulamadı', { duration: 10000 });
                return;
            }
            const blob = new Blob([r.data], { type: 'application/pdf' });
            const track = r.headers['x-bpost-tracking'] || '';
            const cached = r.headers['x-bpost-cached'] === '1';
            toast.success(
                `bpost etiketi ${cached ? 'hazır (ücretsiz)' : 'oluşturuldu'}${track ? ` · ${track}` : ''}`,
                { duration: 6000 },
            );
            setPrintTarget({ blob, title: `bpost — ${order.order_id}` });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'bpost etiketi basma hatası', { duration: 8000 });
        } finally {
            setBpostBusyOrder(null);
        }
    }, [createBpostMut]);

    const removeBpostLabel = useCallback(async (order) => {
        if (!order?.order_id) return;
        if (!window.confirm(`bpost destek etiketini sil?\nNot: bpost portal'daki shipment kalır, sadece Flowbil cache'inden kaldırılır.`)) return;
        try {
            await deleteBpostMut.mutateAsync({ orderId: order.order_id });
            toast.success('bpost etiketi kaldırıldı');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Silme hatası');
        }
    }, [deleteBpostMut]);

    // ─── BOL ETİKET (POST → poll → PDF) — Iter 104: React Query mutations ──
    const openLabelPreview = async (orderIds) => {
        if (!orderIds || orderIds.length === 0) {
            toast.error('Sipariş seçilmedi');
            return;
        }
        setPrintingOrderIds(new Set(orderIds));
        setLabelPreview('loading');
        try {
            const data = await previewMutation.mutateAsync(orderIds);
            setLabelPreview(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Önizleme hatası');
            setLabelPreview(null);
            setPrintingOrderIds(new Set());
        }
    };

    const confirmAndCreateLabels = async () => {
        if (!labelPreview || labelPreview === 'loading') return;
        const validItems = (labelPreview.items || []).filter(i => i.ok);
        if (validItems.length === 0) {
            toast.error('Yazdırılabilir sipariş yok');
            return;
        }
        try {
            const r = await createMutation.mutateAsync(
                validItems.map(i => ({
                    order_id: i.order_id,
                    offer_id: i.offer_id,
                    order_item_ids: i.order_item_ids,
                    label_source: i.label_source,
                })),
            );
            const ct = r.headers['content-type'] || '';
            if (ct.includes('application/json')) {
                const text = await r.data.text();
                let body = {};
                try { body = JSON.parse(text); } catch { /* ignore */ }
                toast.error(body.message || 'Etiket basılamadı', { duration: 12000 });
                return;
            }
            const blob = new Blob([r.data], { type: 'application/pdf' });
            const okN = parseInt(r.headers['x-labels-created'] || '0', 10) || validItems.length;
            const newN = parseInt(r.headers['x-labels-new'] || '0', 10);
            const reusedN = parseInt(r.headers['x-labels-reused'] || '0', 10);
            const failN = r.headers['x-labels-failed'] || '0';
            let msg = `${okN} etiket hazır`;
            if (reusedN > 0) msg += ` (${newN} yeni · ${reusedN} mevcut/ücretsiz)`;
            if (failN !== '0') msg += ` · ${failN} hata`;
            toast.success(msg, { duration: 6000 });
            setPrintTarget({ blob, title: `Bol Labels (${okN})` });
            setLabelPreview(null);
            setSelected(new Set());
            setPrintingOrderIds(new Set());
            // Mutation onSuccess zaten invalidate ediyor, ekstra load gereksiz
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Etiket basma hatası');
            setPrintingOrderIds(new Set());
        }
    };

    const toggleInvoiceRequested = async (order) => {
        const newVal = !order.invoice_requested;
        try {
            await axios.patch(
                `${API}/api/bol/orders/${order.order_id}/invoice-requested`,
                { invoice_requested: newVal }, { headers: hdrs() },
            );
            patchOrders((cur) => cur.map((o) => o.order_id === order.order_id
                ? { ...o, invoice_requested: newVal, invoice_req_source: newVal ? 'manual' : 'none' }
                : o));
            patchCounts((c) => ({ ...c, invoice_requested: (c.invoice_requested || 0) + (newVal ? 1 : -1) }));
            toast.success(newVal ? 'Fatura istegi isaretlendi' : 'Fatura istegi kaldirildi');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Guncellenemedi');
        }
    };

    const runDiagnostic = async () => {
        setDiagLoading(true);
        setScanResult(null);
        try {
            const r = await axios.get(`${API}/api/bol/diagnose`, { headers: hdrs() });
            setDiagResult(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Teşhis başarısız');
        } finally {
            setDiagLoading(false);
        }
    };

    const scanLostLabels = async () => {
        setScanning(true);
        try {
            const r = await axios.get(`${API}/api/bol/labels/scan`, { headers: hdrs() });
            setScanResult(r.data);
            if (r.data.count === 0) toast('Kayıp etiket bulunamadı', { icon: 'ℹ️' });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Tarama hatası');
        } finally {
            setScanning(false);
        }
    };

    const recoverLostLabels = async (orderIds) => {
        try {
            const r = await recoverMutation.mutateAsync({ orderIds, includePickslip: true });
            const ct = r.headers['content-type'] || '';
            if (ct.includes('application/json')) {
                const text = await r.data.text();
                let body = {};
                try { body = JSON.parse(text); } catch { /* ignore */ }
                toast.error(body.message || 'Kurtarma başarısız', { duration: 10000 });
                return;
            }
            const blob = new Blob([r.data], { type: 'application/pdf' });
            const found = r.headers['x-labels-found'] || '?';
            toast.success(`${found} etiket kurtarıldı`, { duration: 6000 });
            setPrintTarget({ blob, title: `Recovered Labels (${found})` });
            setScanResult(null);
            setDiagResult(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kurtarma hatası');
        }
    };

    const toggleSel = (orderId) => {
        setSelected((cur) => {
            const n = new Set(cur);
            if (n.has(orderId)) n.delete(orderId); else n.add(orderId);
            return n;
        });
    };

    const selectablePending = orders.filter((o) => !o.billit_pushed);
    const allPendingSelected = selectablePending.length > 0 &&
        selectablePending.every((o) => selected.has(o.order_id));
    const toggleAll = () => {
        if (allPendingSelected) setSelected(new Set());
        else setSelected(new Set(selectablePending.map((o) => o.order_id)));
    };

    // Iter 110/Faz0: Pagination'da seçim sıfırla — görünmeyen siparişte yanlış toplu aksiyon riski sıfır
    useEffect(() => {
        setSelected(new Set());
    }, [page, statusTab]);

    // Iter 97.3: client-side search filter
    const q = search.trim().toLowerCase();
    let filteredOrders = !q ? orders : orders.filter((o) => {
        const cust = o.customer || {};
        const name = `${cust.company || ''} ${cust.first_name || ''} ${cust.last_name || ''}`.toLowerCase();
        if (name.includes(q)) return true;
        if ((cust.email || '').toLowerCase().includes(q)) return true;
        if ((cust.vat_number || '').toLowerCase().includes(q)) return true;
        if ((o.order_id || '').toLowerCase().includes(q)) return true;
        for (const ln of (o.lines || [])) {
            if ((ln.title || '').toLowerCase().includes(q)) return true;
            if ((ln.ean || '').toLowerCase().includes(q)) return true;
        }
        return false;
    });

    // Iter 113.35: Sub-filter kaldırıldı — tab yapısı zaten "picked/unpicked" ayrımını yapıyor.
    // ('open' = etiket basılmış AND picked_at yok, 'shipped' = picked_at var)

    // Iter 97.4: Pagination
    const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pagedOrders = filteredOrders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // Config yoksa setup CTA goster
    // config henuz yuklenmedi (undefined) ise needsSetup gosterme — sadece dolu gelip client_id yoksa goster
    const needsSetup = config !== undefined && !config?.client_id;

    return (
        <div className="space-y-4" data-testid="ecommerce-tab">
            {/* Header — Iter 110/Faz4 */}
            <EcommerceHeader
                config={config}
                needsSetup={needsSetup}
                syncing={syncing}
                exporting={exporting}
                tracking={trackingRefreshing}
                onSync={sync}
                onExport={exportExcel}
                onTrackingRefresh={refreshTracking}
                onSettings={() => navigate('/settings?tab=bol')}
            />

            {/* Setup CTA */}
            {needsSetup && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3" data-testid="bol-setup-cta">
                    <AlertCircle size={20} className="text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="font-semibold text-amber-900 text-sm">Bol.com henuz yapilandirilmadi</div>
                        <div className="text-xs text-amber-800 mt-1">
                            Bol.com Retailer API client_id ve client_secret bilgilerini Settings'ten girin.
                            <a href="https://partnerplatform.bol.com/en/idp/getting-access-with-api-client-credentials"
                                target="_blank" rel="noreferrer"
                                className="ml-1 underline inline-flex items-center gap-0.5">
                                Nasil olusturulur? <ExternalLink size={10} />
                            </a>
                        </div>
                        <button onClick={() => navigate('/settings?tab=bol')}
                            className="mt-2 h-8 px-3 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md">
                            Ayarlara Git
                        </button>
                    </div>
                </div>
            )}

            {/* Status tabs — Iter 113.37: 5 sekme (Yeni | Hazırlanacak | Gonderildi | Hepsi | Iptal) */}
            {!needsSetup && (
                <div className="flex gap-1 border-b border-gray-200 bg-white rounded-t-xl px-3 pt-2 overflow-x-auto">
                    {[
                        { k: 'new', l: 'Yeni', n: counts.new },
                        { k: 'open', l: 'Hazırlanacak', n: counts.open },
                        { k: 'shipped', l: 'Gonderildi', n: counts.shipped },
                        { k: 'all', l: 'Hepsi', n: counts.all },
                        ...(counts.cancelled > 0 ? [{ k: 'cancelled', l: 'Iptal', n: counts.cancelled }] : []),
                        ...(hideHeader ? [] : [
                            { k: 'dashboard', l: 'Dashboard', icon: BarChart3 },
                            { k: 'profitability', l: 'Karlilik', icon: Wallet },
                            { k: 'customer-qa', l: 'Soru-Yanıt', icon: MessageSquare },
                            { k: 'settings', l: 'Ayarlar', icon: SettingsIcon },
                        ]),
                    ].map((t) => (
                        <button key={t.k} onClick={() => { setStatusTab(t.k); setPage(1); }}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${statusTab === t.k ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                            data-testid={`bol-status-tab-${t.k}`}>
                            {t.icon && <t.icon size={13} />}
                            {t.l}
                            {t.n !== undefined && (
                                <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${statusTab === t.k ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
                                    {t.n}
                                </span>
                            )}
                        </button>
                    ))}
                    <div className="ml-auto py-2 text-xs text-gray-500">
                        {statusTab !== 'dashboard' && statusTab !== 'profitability' && `${filteredOrders.length} / ${orders.length} siparis`}
                    </div>
                </div>
            )}

            {/* Dashboard view */}
            {!needsSetup && statusTab === 'dashboard' && <BolDashboard />}

            {/* Karlilik view */}
            {!needsSetup && statusTab === 'profitability' && <BolProfitability />}

            {/* Customer Q&A — AI manuel paste */}
            {!needsSetup && statusTab === 'customer-qa' && <CustomerQATab />}

            {/* Iter 113.29: Hazırlanacak / Hazırlandı — Kiosk monitör listesi */}
            {false && !needsSetup && (statusTab === 'pick_pending' || statusTab === 'picked') && (
                <PickingListView
                    kind={statusTab}
                    items={statusTab === 'pick_pending' ? pickingItems.pending : pickingItems.picked}
                    stats={pickingStats}
                    loading={pickingLoading}
                    onReload={loadPicking}
                    onUnpick={unpickOne}
                />
            )}

            {/* Iter 113.35: alt filter chip'leri kaldırıldı — Yeni/Gonderilecek/Gonderilmis tab'ları zaten aynı ayrımı yapıyor */}

            {/* Settings view — Iter 104e: PrintNode + diğer ayarlar */}
            {!needsSetup && statusTab === 'settings' && (
                <div className="space-y-4">
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <div className="text-sm font-semibold text-gray-800 mb-1">Bol API Teşhis</div>
                        <div className="text-xs text-gray-500 mb-3">Bol Retailer API izinlerini ve kayıp etiketleri test et</div>
                        {/* Iter 110/Faz5: Son diagnose statusu */}
                        {config?.last_diagnose && (
                            <div className={`mb-3 p-2.5 rounded-md text-xs flex items-center gap-2 ${
                                config.last_diagnose.verdict === 'ok'
                                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                                    : config.last_diagnose.verdict === 'warn'
                                        ? 'bg-amber-50 border border-amber-200 text-amber-900'
                                        : 'bg-rose-50 border border-rose-200 text-rose-900'
                            }`} data-testid="bol-last-diagnose">
                                <span className="font-semibold">
                                    {config.last_diagnose.verdict === 'ok' ? '✓ ' :
                                        config.last_diagnose.verdict === 'warn' ? '⚠ ' : '✗ '}
                                    Son teşhis: {config.last_diagnose.passing}/{config.last_diagnose.tested} test başarılı
                                </span>
                                <span className="opacity-60 ml-auto">
                                    {config.last_diagnose.at?.slice(0, 16).replace('T', ' ')}
                                </span>
                            </div>
                        )}
                        <button
                            onClick={runDiagnostic}
                            disabled={diagLoading}
                            className="h-9 px-3 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                            data-testid="bol-diagnose-btn">
                            {diagLoading ? <Loader2 size={14} className="animate-spin" /> : <AlertCircle size={14} />}
                            Teşhis Çalıştır
                        </button>
                    </div>
                    <PrintNodeSettings />
                    <AutoPrintSettings />
                </div>
            )}

            {/* Filter bar — Iter 110/Faz4 */}
            {!needsSetup && statusTab !== 'dashboard' && statusTab !== 'profitability' && statusTab !== 'settings' && statusTab !== 'pick_pending' && statusTab !== 'picked' && statusTab !== 'customer-qa' && (
                <>
                    <AutoPrintBanner onGoSettings={() => setStatusTab('settings')} />
                    <EcommerceFilters
                        search={search}
                        onSearchChange={(v) => { setSearch(v); setPage(1); }}
                        onlyPending={onlyPending}
                        onTogglePending={() => { setOnlyPending((v) => !v); setPage(1); }}
                    />
                </>
            )}

            {/* Orders table */}
            {statusTab !== 'dashboard' && statusTab !== 'profitability' && statusTab !== 'settings' && statusTab !== 'pick_pending' && statusTab !== 'picked' && statusTab !== 'customer-qa' && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-gray-400">
                        <Loader2 size={20} className="animate-spin mx-auto" />
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="p-12 text-center text-gray-400">
                        <ShoppingCart size={36} className="mx-auto mb-2 opacity-40" />
                        <div className="text-sm mb-3">
                            {needsSetup
                                ? 'Once Bol.com ayarlarini yapin'
                                : q
                                    ? `"${search}" icin sonuc bulunamadi`
                                    : 'Henüz sipariş yok'}
                        </div>
                        {!needsSetup && !q && (
                            <button
                                onClick={sync}
                                disabled={syncing}
                                className="h-9 px-4 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-1.5"
                                data-testid="empty-sync-btn">
                                {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                                Bol.com'dan Çek
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm hidden md:table">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="py-1.5 px-2 w-8">
                                    <input type="checkbox" checked={allPendingSelected}
                                        onChange={toggleAll} data-testid="select-all-pending" />
                                </th>
                                <th className="py-1.5 px-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">Sipariş</th>
                                <th className="py-1.5 px-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">Müşteri</th>
                                <th className="py-1.5 px-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wide">Ürünler</th>
                                <th className="py-1.5 px-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">Tutar</th>
                                <th className="py-1.5 px-2 text-center text-[10px] font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">Kargo</th>
                                <th className="py-1.5 px-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wide w-[195px] sticky right-0 bg-gray-50 z-10 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">Aksiyon</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {pagedOrders.map((o) => {
                                const cust = o.customer || {};
                                const name = cust.company || `${cust.first_name || ''} ${cust.last_name || ''}`.trim() || '—';
                                const itemCount = (o.lines || []).length;
                                const totalQty = (o.lines || []).reduce((a, l) => a + (l.quantity || 0), 0);
                                const firstTitle = o.lines?.[0]?.title || '';
                                const pushed = o.billit_pushed;
                                const isOpen = expanded === o.order_id;
                                const location = [(cust.address || {}).city, (cust.address || {}).country].filter(Boolean).join(', ');
                                return (
                                    <React.Fragment key={o.order_id}>
                                        <tr className={`group hover:bg-gray-50 cursor-pointer ${pushed ? 'opacity-70' : ''} ${isOpen ? 'bg-gray-50' : ''}`}
                                            onClick={() => setExpanded(isOpen ? null : o.order_id)}
                                            data-testid={`bol-row-${o.order_id}`}>
                                            <td className="py-1.5 px-2" onClick={(e) => e.stopPropagation()}>
                                                {!pushed && (
                                                    <input type="checkbox" checked={selected.has(o.order_id)}
                                                        onChange={() => toggleSel(o.order_id)}
                                                        data-testid={`bol-sel-${o.order_id}`} />
                                                )}
                                            </td>
                                            {/* Sipariş & Tarih combined */}
                                            <td className="py-1.5 px-2 whitespace-nowrap">
                                                <div className="font-mono text-xs font-semibold text-gray-900">{o.order_id}</div>
                                                <div className="text-[10px] text-gray-500 flex items-center gap-1.5">
                                                    <span>{fmtDt(o)}</span>
                                                    {(o.bol_shipping_label_id || o.bol_label_posted_at) && (
                                                        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded text-[9px] font-bold uppercase tracking-wide"
                                                            title={o.bol_label_posted_at ? `Etiket basıldı: ${o.bol_label_posted_at.slice(0, 16).replace('T', ' ')}` : 'Etiket basıldı'}
                                                            data-testid={`bol-printed-badge-${o.order_id}`}>
                                                            <Printer size={8} /> Yazdırıldı
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            {/* Müşteri & Lokasyon combined */}
                                            <td className="py-1.5 px-2 whitespace-nowrap">
                                                <div className="text-gray-900 font-medium truncate max-w-[180px] text-xs">{name}</div>
                                                <div className="text-[10px] text-gray-500 truncate max-w-[180px]">{location || '—'}</div>
                                            </td>
                                            {/* Ürünler & Adet combined */}
                                            <td className="py-1.5 px-2 max-w-[260px]">
                                                <div className="text-gray-700 truncate text-xs">{firstTitle}</div>
                                                <div className="text-[10px] text-gray-500">
                                                    {totalQty} adet{itemCount > 1 ? ` · ${itemCount} ürün` : ''}
                                                </div>
                                            </td>
                                            {/* Tutar & Komisyon combined */}
                                            <td className="py-1.5 px-2 text-right whitespace-nowrap">
                                                <div className="font-semibold text-gray-900 text-xs">{fmt(o.total_incl_vat)} €</div>
                                                {o.estimated_commission ? (
                                                    <div className="text-[10px] text-rose-700">-{fmt(o.estimated_commission)} €</div>
                                                ) : null}
                                            </td>
                                            <td className="py-1.5 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                                                <TrackBadge tracking={o.tracking} listStatus={o.list_status} testId={`bol-track-${o.order_id}`} />
                                            </td>
                                            <td className={`py-1.5 px-2 text-right whitespace-nowrap w-[195px] sticky right-0 z-[5] shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.12)] ${isOpen ? 'bg-gray-50' : 'bg-white'} group-hover:bg-gray-50`} onClick={(e) => e.stopPropagation()}>
                                                <OrderActions
                                                    order={o}
                                                    isPrinting={printingOrderIds.has(o.order_id)}
                                                    isBpostBusy={bpostBusyOrder === o.order_id}
                                                    isPushing={pushingId === o.order_id}
                                                    bpostConfigured={bpostConfigured}
                                                    onPrint={() => openLabelPreview([o.order_id])}
                                                    onBpostPrint={printBpostLabel}
                                                    onBpostRemove={removeBpostLabel}
                                                    onPush={pushOne}
                                                    onPickSlip={(ord) => printPickSlips([ord.order_id])}
                                                />
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <OrderDetailRow
                                                order={o}
                                                name={name}
                                                cust={cust}
                                                pushed={pushed}
                                                pushingId={pushingId}
                                                onPush={pushOne}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                    {/* Mobile card view — kompakt, dokunmatik dostu */}
                    <div className="md:hidden divide-y divide-gray-100">
                        {pagedOrders.map((o) => {
                            const cust = o.customer || {};
                            const name = (cust.first_name || '') + ' ' + (cust.last_name || '');
                            const firstLine = (o.lines || [])[0] || {};
                            const itemCount = (o.lines || []).length;
                            const totalQty = (o.lines || []).reduce((a, l) => a + (l.quantity || 0), 0);
                            const loc = [(cust.address || {}).city, (cust.address || {}).country].filter(Boolean).join(', ');
                            const hasLabel = !!(o.bol_shipping_label_id || o.tracking?.code || o.tracking?.latest_event);
                            return (
                                <div key={o.order_id} className="p-3 active:bg-gray-50" data-testid={`bol-card-${o.order_id}`}>
                                    {/* Üst: checkbox + sipariş & tutar */}
                                    <div className="flex items-start gap-2 mb-2">
                                        {!o.billit_pushed && (
                                            <input type="checkbox" checked={selected.has(o.order_id)}
                                                onChange={() => toggleSel(o.order_id)}
                                                className="mt-1 h-4 w-4"
                                                data-testid={`bol-sel-mobile-${o.order_id}`} />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[11px] font-mono font-semibold text-gray-700">{o.order_id}</span>
                                                {hasLabel && (
                                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">ETİKETLİ</span>
                                                )}
                                            </div>
                                            <div className="text-sm font-bold text-gray-900 truncate">{name.trim() || cust.company || '—'}</div>
                                            <div className="text-[11px] text-gray-500 truncate">{loc || '—'}</div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-base font-black text-gray-900">{(o.total_incl_vat || 0).toFixed(2)} €</div>
                                            <div className="text-[10px] text-gray-500">{fmtDt(o)}</div>
                                        </div>
                                    </div>

                                    {/* Ürün özeti */}
                                    {firstLine.title && (
                                        <div className="text-xs text-gray-600 mb-2 line-clamp-1 pl-6">
                                            <span className="font-semibold">{totalQty}×</span> {firstLine.title}
                                            {itemCount > 1 && <span className="text-gray-400"> +{itemCount - 1}</span>}
                                        </div>
                                    )}

                                    {/* Kargo + butonlar */}
                                    <div className="flex items-center gap-2 mt-2">
                                        <TrackBadge tracking={o.tracking} listStatus={o.list_status} testId={`bol-track-mobile-${o.order_id}`} />
                                        <div className="ml-auto">
                                            <OrderActions
                                                order={o}
                                                compact={true}
                                                isPrinting={printingOrderIds.has(o.order_id)}
                                                isBpostBusy={bpostBusyOrder === o.order_id}
                                                isPushing={pushingId === o.order_id}
                                                bpostConfigured={bpostConfigured}
                                                onPrint={() => openLabelPreview([o.order_id])}
                                                onBpostPrint={printBpostLabel}
                                                onBpostRemove={removeBpostLabel}
                                                onPush={pushOne}
                                                onPickSlip={(ord) => printPickSlips([ord.order_id])}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    </div>
                )}
            </div>
            )}

            {/* Pagination */}
            {!needsSetup && statusTab !== 'dashboard' && statusTab !== 'profitability' && statusTab !== 'settings' && statusTab !== 'pick_pending' && statusTab !== 'picked' && statusTab !== 'customer-qa' && filteredOrders.length > PAGE_SIZE && (
                <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-between text-sm" data-testid="bol-pagination">
                    <div className="text-xs text-gray-600">
                        Sayfa <b>{currentPage}</b> / {totalPages} · {filteredOrders.length} siparis
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1}
                            className="h-8 px-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
                            data-testid="page-prev">
                            <ChevronLeft size={14} /> Onceki
                        </button>
                        <div className="px-3 text-xs">{currentPage} / {totalPages}</div>
                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
                            className="h-8 px-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
                            data-testid="page-next">
                            Sonraki <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            )}

            {/* Label print confirmation modal — Bol fiyatı önce göster, sonra onay */}
            <LabelPreviewDialog
                labelPreview={labelPreview}
                printingLabels={printingLabels}
                onClose={() => { setLabelPreview(null); setPrintingOrderIds(new Set()); }}
                onConfirm={confirmAndCreateLabels}
            />

            {/* Bol Diagnostic Modal */}
            <DiagnoseDialog
                diagResult={diagResult}
                onClose={() => setDiagResult(null)}
                scanResult={scanResult}
                setScanResult={setScanResult}
                scanning={scanning}
                onScanLostLabels={scanLostLabels}
                recovering={recovering}
                onRecoverLostLabels={recoverLostLabels}
            />

            {/* Split Shipment Dialog — Iter 109'da kaldırıldı (bpost destek etiketi yeterli) */}

            {/* Sticky Bottom Bulk Bar — Iter 110/Faz4 */}
            {!needsSetup && (
                <BulkActionBar
                    count={selected.size}
                    onClear={() => setSelected(new Set())}
                    onPrint={() => openLabelPreview(Array.from(selected))}
                    onPickSlip={() => printPickSlips(Array.from(selected))}
                    onPush={pushSelected}
                    busyPrint={printingLabels}
                    busyPush={bulkPushing}
                />
            )}

            {/* Print Target Dialog (Iter 104e) — PrintNode veya browser print */}
            {printTarget && (
                <PrintTargetDialog
                    pdfBlob={printTarget.blob}
                    title={printTarget.title}
                    copies={printTarget.copies}
                    onClose={() => setPrintTarget(null)}
                />
            )}
        </div>
    );
}

// Iter 113.29: Hazırlanacak / Hazırlandı — kompakt liste görünümü (Kiosk monitör ile aynı veri)
function PickingListView({ kind, items, loading, onReload, onUnpick }) {
    const isPending = kind === 'pick_pending';
    return (
        <div className="bg-white border border-gray-200 rounded-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <div>
                    <div className="text-sm font-bold text-gray-900">
                        {isPending ? 'Hazırlanacak Siparişler' : 'Hazırlanan Siparişler'}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                        {isPending
                            ? 'Etiketi basılmış, depoya düşen bekleyen siparişler. Depo monitörü ile senkron.'
                            : 'Barcode taranarak veya manuel olarak hazırlandı işaretlenen siparişler.'}
                    </div>
                </div>
                <button onClick={onReload} disabled={loading}
                    className="h-8 px-3 text-xs font-semibold bg-gray-900 text-white rounded hover:bg-black disabled:opacity-50"
                    data-testid={`picking-${kind}-refresh`}>
                    {loading ? '...' : 'Yenile'}
                </button>
            </div>
            {loading && items.length === 0 ? (
                <div className="p-12 text-center text-gray-400 text-sm">Yükleniyor...</div>
            ) : items.length === 0 ? (
                <div className="p-12 text-center text-gray-400 text-sm">
                    {isPending ? 'Bekleyen sipariş yok — tümü hazır.' : 'Henüz hazırlanmış sipariş yok.'}
                </div>
            ) : (
                <table className="w-full text-sm" data-testid={`picking-${kind}-table`}>
                    <thead>
                        <tr className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
                            <th className="text-left px-3 py-2 font-bold">Sipariş</th>
                            <th className="text-left px-3 py-2 font-bold">Müşteri</th>
                            <th className="text-left px-3 py-2 font-bold">Şehir · Ülke</th>
                            <th className="text-left px-3 py-2 font-bold">Kargo</th>
                            <th className="text-left px-3 py-2 font-bold">Track & Trace</th>
                            <th className="text-right px-3 py-2 font-bold">Kalem</th>
                            {isPending
                                ? <th className="text-right px-3 py-2 font-bold">Bekleme</th>
                                : <><th className="text-left px-3 py-2 font-bold">Hazırlandı</th><th className="text-center px-3 py-2 font-bold">Yön.</th></>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((o) => (
                            <tr key={o.order_id} className="border-t border-gray-100 hover:bg-gray-50/60">
                                <td className="px-3 py-2 font-mono text-xs">#{o.order_id_short || (o.order_id || '').slice(-6)}</td>
                                <td className="px-3 py-2">{o.customer_name || '—'}</td>
                                <td className="px-3 py-2 text-gray-600 text-xs">{o.city || '—'} · {o.country || ''}</td>
                                <td className="px-3 py-2 text-gray-600 text-xs">{o.transporter_code || '—'}</td>
                                <td className="px-3 py-2 font-mono text-[10px] text-gray-500">{o.track_and_trace || '—'}</td>
                                <td className="px-3 py-2 text-right">{o.line_count || 0}</td>
                                {isPending ? (
                                    <td className="px-3 py-2 text-right text-xs">
                                        {o.order_date ? _minAgo(o.order_date) : '—'}
                                    </td>
                                ) : (
                                    <>
                                        <td className="px-3 py-2 text-xs text-emerald-700">
                                            {o.picked_at ? new Date(o.picked_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                                            {o.picked_via && <span className="ml-1 text-[10px] text-gray-400">({o.picked_via})</span>}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button onClick={() => onUnpick(o.order_id)}
                                                className="text-[10px] font-semibold text-rose-600 hover:text-rose-800 hover:underline"
                                                data-testid={`picking-unpick-${o.order_id}`}>
                                                geri al
                                            </button>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function _minAgo(iso) {
    const d = new Date(iso);
    const min = Math.floor((Date.now() - d.getTime()) / 60000);
    if (min < 60) return `${min}dk`;
    const h = Math.floor(min / 60);
    return `${h}s ${min % 60}dk`;
}
